import { useCallback, useEffect, useRef, useState } from 'react'
import { Avatar } from '../../../components/Avatar'
import { PageHeader } from '../../../components/PageHeader'
import { RoomQR } from '../../../components/RoomQR'
import { useAuth } from '../../../lib/auth'
import {
  advancePhase, stepBackPresenter, castCourseVote, fetchRoomSnapshot, leaveRoom,
  startGame, submitSingleCourse, subscribeToRoom, kickIetPlayer,
  type RoomSnapshot,
} from '../../../lib/api/rooms'
import {
  fetchAiPlayers, addAiPlayer, removeAiPlayer,
  submitCourseAsAi, castCourseVoteAsAi, generateCourseFromAi,
} from '../../../lib/api/ai'
import {
  generateSingleCourseFor, decideVoteFor, partitionHandForCourses,
} from '../../../lib/ai/personas'
import type { AiPlayerRow } from '../../../lib/database.types'
import { StoryBuilderScreen } from './StoryBuilderScreen'
import { PresentationScreen } from './PresentationScreen'
import { ResultsScreen } from './ResultsScreen'
import { readError } from '../../../lib/errors'
import { useProfileAvatars } from '../../../lib/useProfileAvatars'
import { supabase } from '../../../lib/supabase'
import { BackgroundTitle } from '../../../components/BackgroundTitle'
import { useT } from '../../../i18n'

interface Props {
  roomId: string
  onExit: () => void
}

interface CourseData { text: string; usedCards: string[] }

/** Returns set of ingredient IDs already consumed in courses before `course`. */
function usedIngredientIds(
  sub: { course1_ids: string[]; course2_ids: string[] } | null | undefined,
  course: 1 | 2 | 3,
): Set<string> {
  const used = new Set<string>()
  if (!sub) return used
  if (course > 1) sub.course1_ids.forEach(id => used.add(id))
  if (course > 2) sub.course2_ids.forEach(id => used.add(id))
  return used
}

/** Non-empty-text check for a specific course on a submission row. */
function courseTextFor(
  sub: { course1_text: string; course2_text: string; course3_text: string } | null | undefined,
  course: 1 | 2 | 3,
): string {
  if (!sub) return ''
  return course === 1 ? sub.course1_text
       : course === 2 ? sub.course2_text
       :                sub.course3_text
}

/**
 * Single-room orchestrator. Subscribes to realtime changes on the room,
 * its players, submissions, and votes — then re-fetches a full snapshot
 * on every event. The rendered sub-screen is picked from room.status.
 *
 * Host has the only "advance" control; other players just watch the
 * state transition when the server updates.
 */
export function LiveRoomScreen({ roomId, onExit }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const [snap, setSnap] = useState<RoomSnapshot | null>(null)
  // Photos for the human seats — member rows only carry display_name.
  const avatars = useProfileAvatars((snap?.players ?? []).filter(p => !p.is_ai).map(p => p.profile_id))
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [aiPersonas, setAiPersonas] = useState<AiPlayerRow[]>([])
  const [aiAddBusyId, setAiAddBusyId] = useState<string | null>(null)
  // Last autoplay error/status, surfaced in the host's UI so silent RPC
  // failures don't leave the room frozen.
  const [aiStatus, setAiStatus] = useState<string | null>(null)
  const cancelledRef = useRef(false)
  // Prevents the autoplay effect from hammering the server while an
  // in-flight submission/vote is still resolving: the realtime tick that
  // comes back would otherwise retrigger us before the DB reflects the
  // first insert.
  const autoplayBusyRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    function refetch() {
      fetchRoomSnapshot(roomId)
        .then(s => { if (!cancelledRef.current) setSnap(s) })
        .catch(e => { if (!cancelledRef.current) setError(readError(e)) })
    }
    refetch()
    const unsub = subscribeToRoom(roomId, refetch)
    // Polling + resume fallback (same rationale as TokyoMetroRoomScreen):
    // iOS suspends the JS runtime when the app backgrounds, the websocket
    // dies, and missed realtime events are never replayed — without these
    // a returning player stares at a frozen room until they force-quit.
    const poll = setInterval(refetch, 5000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelledRef.current = true
      unsub()
      clearInterval(poll)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [roomId])

  // Personalities for the Add AI picker. Fetched once — the list is
  // small and rarely changes (seeded by migration).
  useEffect(() => {
    fetchAiPlayers()
      .then(rows => { if (!cancelledRef.current) setAiPersonas(rows) })
      .catch(() => { /* non-fatal; picker will just show empty state */ })
  }, [])

  async function handleAddAi(aiPlayerId: string) {
    if (aiAddBusyId) return
    setAiAddBusyId(aiPlayerId); setError(null)
    try {
      await addAiPlayer(roomId, aiPlayerId)
    } catch (e) { setError(readError(e)) }
    finally { setAiAddBusyId(null) }
  }

  async function handleRemoveAi(playerId: string) {
    setError(null)
    try { await removeAiPlayer(playerId) }
    catch (e) { setError(readError(e)) }
  }

  async function handleLeave() {
    try { await leaveRoom(roomId) } catch { /* best effort */ }
    onExit()
  }

  async function handleStart() {
    if (busy) return
    setBusy(true); setError(null)
    try { await startGame(roomId) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function handleAdvance() {
    if (busy) return
    setBusy(true); setError(null)
    try {
      // Pass the phase we're looking at so a stale tap from another
      // player no-ops instead of double-advancing the machine.
      const rm = snap?.room
      await advancePhase(roomId, rm
        ? { status: rm.status, course: rm.active_course, index: rm.current_player_index }
        : undefined)
    }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function handleStepBack() {
    if (busy) return
    setBusy(true); setError(null)
    try { await stepBackPresenter(roomId, snap?.room.current_player_index) }
    catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function handleSkipPlayer(playerId: string) {
    try { await kickIetPlayer(playerId) }
    catch (e) { setError(readError(e)) }
  }

  async function handleSubmitCourse(course: 1 | 2 | 3, data: CourseData) {
    if (busy) return
    setBusy(true); setError(null)
    try {
      await submitSingleCourse(roomId, course, data.text, data.usedCards)
    } catch (e) { setError(readError(e)) }
    finally { setBusy(false) }
  }

  async function handleVote(submissionId: string, course: 1 | 2 | 3, wouldEat: boolean) {
    try { await castCourseVote(submissionId, course, wouldEat) }
    catch (e) { setError(readError(e)) }
  }

  // ── AI autoplay ────────────────────────────────────────────────────────
  // Fills AI submissions during `building` and casts AI votes during
  // `presenting`/`voting` for the current course. Both server RPCs are
  // idempotent (upsert / on-conflict-do-update) so re-running between
  // realtime ticks is safe.
  //
  // We expose it as a callable so the host can also hit a "Nudge AI"
  // button when it looks stuck — otherwise we rely on it firing from the
  // subscription effect below.
  const runAutoplay = useCallback(async () => {
    if (!snap || autoplayBusyRef.current) return
    // Any present (non-AI, non-skipped) human drives the AI, so the bots
    // keep playing even if the host drops. Idempotent RPCs make it safe
    // for several humans to drive at once.
    const meRow = snap.players.find(p => p.profile_id === profile?.id)
    if (!meRow || meRow.is_ai || meRow.skipped) return

    const { room, players, submissions, courseVotes } = snap
    const aiInRoom = players.filter(p => p.is_ai && !p.skipped)
    if (aiInRoom.length === 0) return

    // If we have AI seats but the persona catalog hasn't loaded yet, bail
    // loudly — otherwise the host sees votes stall with no explanation.
    if (aiPersonas.length === 0) {
      setAiStatus(t('Loading AI personalities…'))
      return
    }

    autoplayBusyRef.current = true
    let didSomething = false
    try {
      if (room.status === 'building') {
        // Only write the currently-active course — the room loops back
        // to `building` for each course in turn, so we don't want to
        // pre-fill everything on the first pass.
        const course = (Math.max(1, Math.min(3, room.active_course)) as 1 | 2 | 3)
        for (const p of aiInRoom) {
          if (!p.ai_player_id) continue
          const persona = aiPersonas.find(a => a.id === p.ai_player_id)
          if (!persona) continue
          const already = submissions.find(s => s.player_id === p.id)
          if (courseTextFor(already, course).trim().length > 0) continue

          // Partition the original hand into the three courses ONCE per
          // (room, player). Every card lands in exactly one course, so
          // by the end of course 3 the AI has used every ingredient in
          // their hand — and never any ingredient outside it.
          const [c1ids, c2ids, c3ids] = partitionHandForCourses(
            p.hand, `${room.id}:${p.id}:partition`,
          )
          const courseIds = course === 1 ? c1ids : course === 2 ? c2ids : c3ids

          // Defensive: if the partition skipped anything (can't happen
          // with the current helper, but kept for safety), sweep up any
          // cards not consumed earlier and shove them into dessert.
          const aiUsedBefore = usedIngredientIds(already, course)
          let cardsForCourse = courseIds.filter(id => !aiUsedBefore.has(id))
          if (course === 3) {
            const everythingLeft = p.hand.filter(id => !aiUsedBefore.has(id))
            const seen = new Set(cardsForCourse)
            for (const id of everythingLeft) if (!seen.has(id)) cardsForCourse.push(id)
          }

          setAiStatus(t('{name} is plating course {course}…', { name: p.display_name, course }))

          // Branch on persona.model: if it's a real Claude id, ask the
          // edge fn to generate. Otherwise use the offline template.
          // If the edge call fails for any reason we fall back to local
          // so the game never stalls on an AI seat.
          const wantsLlm = typeof persona.model === 'string' && persona.model.startsWith('claude-')
          let finalText = ''
          let finalIds: string[] = []
          if (wantsLlm) {
            try {
              const ai = await generateCourseFromAi(room.id, p.id, course)
              if (ai.text && ai.ids.length > 0) {
                finalText = ai.text
                finalIds = ai.ids
              }
            } catch (e) {
              console.warn('[iet-ai] edge fn failed, falling back to local:', e)
            }
          }
          if (!finalText) {
            const pick = generateSingleCourseFor(
              persona.style_key,
              cardsForCourse,
              course,
              `${room.id}:${p.id}:${course}`,
            )
            finalText = pick.text
            finalIds = pick.ids
          }
          await submitCourseAsAi(p.id, course, finalText, finalIds)
          didSomething = true
        }
      } else if (room.status === 'presenting' || room.status === 'voting') {
        const activePlayers = players.filter(p => !p.skipped)
        const presenter = activePlayers[room.current_player_index]
        if (!presenter) return
        const presenterSub = submissions.find(s => s.player_id === presenter.id)
        if (!presenterSub) return

        const courseText =
          room.active_course === 1 ? presenterSub.course1_text :
          room.active_course === 2 ? presenterSub.course2_text :
                                      presenterSub.course3_text

        for (const p of aiInRoom) {
          if (!p.ai_player_id) continue
          if (p.id === presenter.id) continue  // presenter doesn't vote
          const persona = aiPersonas.find(a => a.id === p.ai_player_id)
          if (!persona) continue
          const already = courseVotes.find(cv =>
            cv.submission_id === presenterSub.id &&
            cv.course_number === room.active_course &&
            cv.voter_id === p.id
          )
          if (already) continue

          setAiStatus(t('{name} is deciding…', { name: p.display_name }))
          const wouldEat = decideVoteFor(
            persona.yes_rate,
            p.id,
            presenterSub.id,
            room.active_course as 1 | 2 | 3,
            courseText ?? '',
          )
          await castCourseVoteAsAi(
            p.id,
            presenterSub.id,
            room.active_course as 1 | 2 | 3,
            wouldEat,
          )
          didSomething = true
        }
      }
      // Only clear the status if we actually landed a move this pass —
      // otherwise a stale "loading" pill would stay up.
      if (didSomething) setAiStatus(null)
      else setAiStatus(prev => (prev?.startsWith(t('AI error')) ? prev : null))
    } catch (e) {
      setAiStatus(t('AI error') + `: ${readError(e)}`)
    } finally {
      autoplayBusyRef.current = false
    }
  }, [snap, aiPersonas, profile?.id])

  useEffect(() => { runAutoplay() }, [runAutoplay])

  // ── Loading / error states ─────────────────────────────────────────────
  if (!snap) {
    return (
      <StatusScreen
        title={t('Connecting...')}
        subtitle={t('Joining the table')}
        error={error}
        onBack={onExit}
      />
    )
  }

  const { room, players, submissions, courseVotes } = snap
  const me = players.find(p => p.profile_id === profile?.id) ?? null
  const isHost = room.host_id === profile?.id
  // Skipped (dropped) seats are excluded from the everyone-in / presenter
  // counts so a drop never blocks the table. Any seated player can drive
  // advancement, so the game never waits on the host.
  const activePlayers = players.filter(p => !p.skipped)
  const iCanControl = Boolean(me)
  const mySubmission = me ? submissions.find(s => s.player_id === me.id) ?? null : null

  if (room.status === 'abandoned') {
    return (
      <StatusScreen
        title={t('Table closed')}
        subtitle={t('Everyone left, so the room was abandoned.')}
        onBack={onExit}
      />
    )
  }

  if (!me) {
    return (
      <StatusScreen
        title={t("You're no longer in this room")}
        subtitle={t('Head back to find or create another table.')}
        onBack={onExit}
      />
    )
  }

  // ── Lobby ──────────────────────────────────────────────────────────────
  if (room.status === 'lobby') {
    const humanCount = players.filter(p => !p.is_ai).length
    const seatedAiIds = new Set(players.filter(p => p.is_ai).map(p => p.ai_player_id!).filter(Boolean))
    const availablePersonas = aiPersonas.filter(p => !seatedAiIds.has(p.id))
    const myRow = players.find(p => p.profile_id === profile?.id) ?? null
    return (
      <LobbyView
        roomId={room.id}
        code={room.code}
        players={players.map(p => {
          const persona = p.ai_player_id
            ? aiPersonas.find(a => a.id === p.ai_player_id) ?? null
            : null
          return {
            id: p.id,
            name: p.display_name,
            isAi: p.is_ai,
            isHost: p.profile_id === room.host_id,
            isMe: p.profile_id === profile?.id,
            avatarEmoji: persona?.avatar_emoji ?? null,
            avatarUrl: !p.is_ai && p.profile_id ? avatars[p.profile_id] ?? null : null,
          }
        })}
        myDisplayName={myRow?.display_name ?? null}
        isHost={isHost}
        busy={busy}
        error={error}
        aiStatus={aiStatus}
        humanCount={humanCount}
        availablePersonas={availablePersonas}
        addingAiId={aiAddBusyId}
        onAddAi={handleAddAi}
        onRemoveAi={handleRemoveAi}
        onStart={handleStart}
        onLeave={handleLeave}
      />
    )
  }

  // ── Building (everyone writing the current course) ──────────────────
  if (room.status === 'building' || room.status === 'dealing') {
    const course = (Math.max(1, Math.min(3, room.active_course)) as 1 | 2 | 3)
    const courseIdx = (course - 1) as 0 | 1 | 2
    const courseName = ['Appetizer', t('Main Course'), 'Dessert'][courseIdx]

    // Ingredients consumed in earlier courses are no longer available.
    const usedIds = usedIngredientIds(mySubmission, course)
    const availableHand = me.hand.filter(id => !usedIds.has(id))
    const mustUseAll = course === 3 // dessert = use everything remaining

    const activeIds = new Set(activePlayers.map(p => p.id))
    const submittedCount = submissions.filter(s =>
      activeIds.has(s.player_id) && courseTextFor(s, course).trim().length > 0,
    ).length
    const iSubmitted = courseTextFor(mySubmission, course).trim().length > 0
    const everyoneIn = submittedCount === activePlayers.length
    const aiPendingSubmit = activePlayers.filter(p => {
      if (!p.is_ai) return false
      const sub = submissions.find(s => s.player_id === p.id)
      return courseTextFor(sub, course).trim().length === 0
    }).length

    if (iSubmitted) {
      const outstanding = activePlayers.filter(p =>
        !p.is_ai && p.id !== me.id
        && courseTextFor(submissions.find(s => s.player_id === p.id), course).trim().length === 0,
      )
      return (
        <WaitingView
          title={t('Your {course} is in the kitchen', { course: courseName.toLowerCase() })}
          subtitle={
            everyoneIn
              ? t('Everyone has submitted their {course}. Ready to present!', { course: courseName.toLowerCase() })
              : t('{done} of {total} have written their {course}', {
                  done: submittedCount, total: activePlayers.length, course: courseName.toLowerCase(),
                })
          }
          isHost={iCanControl}
          canAdvance={everyoneIn}
          busy={busy}
          error={error}
          aiStatus={aiStatus}
          aiPendingSubmit={aiPendingSubmit}
          advanceLabel={t('Present the {course} →', { course: courseName })}
          onAdvance={handleAdvance}
          onNudgeAi={runAutoplay}
          onLeave={handleLeave}
          outstanding={outstanding.map(p => ({ id: p.id, name: p.display_name }))}
          onSkipPlayer={handleSkipPlayer}
        />
      )
    }

    return (
      <div className="screen">
        <BackgroundTitle text="I'd Eat That" color="#B58615" />
        <PageHeader
          title="I'd Eat That"
          subtitle={t('Write your {course} · Room {code} · {done}/{total} in', {
            course: courseName.toLowerCase(), code: room.code,
            done: submittedCount, total: activePlayers.length,
          })}
          onBack={onExit}
        />
        <StoryBuilderScreen
          hand={availableHand}
          courseIndex={courseIdx}
          mustUseAll={mustUseAll}
          onSubmit={data => handleSubmitCourse(course, data)}
        />
        {error && (
          <p className="text-sm text-brand-danger text-center py-2">{error}</p>
        )}
      </div>
    )
  }

  // ── Presenting (one player at a time, course by course) ──────────────
  if (room.status === 'presenting' || room.status === 'voting') {
    const presenter = activePlayers[room.current_player_index] ?? activePlayers[0]
    const presenterSub = presenter
      ? submissions.find(s => s.player_id === presenter.id) ?? null
      : null
    const courseIdx = (Math.max(1, room.active_course) - 1) as 0 | 1 | 2
    const courseText =
      courseIdx === 0 ? presenterSub?.course1_text :
      courseIdx === 1 ? presenterSub?.course2_text :
                        presenterSub?.course3_text
    const isMyTurn = presenter?.profile_id === profile?.id
    const totalVoters = Math.max(0, activePlayers.length - 1)

    const votesForCourse = courseVotes.filter(
      cv => cv.submission_id === presenterSub?.id
         && cv.course_number === room.active_course
    )
    const yesCount = votesForCourse.filter(cv => cv.would_eat).length
    const noCount  = votesForCourse.filter(cv => !cv.would_eat).length
    const iVoted   = votesForCourse.some(cv => cv.voter_id === me.id)

    const aiVotersWaiting = activePlayers.filter(p =>
      p.is_ai && p.id !== presenter?.id &&
      !votesForCourse.some(cv => cv.voter_id === p.id),
    ).length

    // Label for the advance button reflects what happens next in the
    // per-course state machine: cycle presenters → write next course →
    // finally see results.
    const isLastPresenter = room.current_player_index >= activePlayers.length - 1
    const nextCourseName =
      room.active_course === 1 ? t('Main Course') :
      room.active_course === 2 ? 'Dessert' : ''
    const advanceLabel =
      !isLastPresenter             ? t('Next Presenter →') :
      room.active_course < 3       ? t('Write the {course} ✍️', { course: nextCourseName }) :
                                     t('See Results 🎉')

    return (
      <div className="screen">
        <BackgroundTitle text="I'd Eat That" color="#B58615" />
        <PageHeader
          title="I'd Eat That"
          subtitle={t('Course {n} · {name} · Room {code}', {
            n: room.active_course, name: presenter?.display_name ?? '', code: room.code,
          })}
          onBack={onExit}
        />
        <PresentationScreen
          presenterName={presenter?.display_name ?? ''}
          presenterIsAi={presenter?.is_ai ?? false}
          presenterAvatar={presenter && !presenter.is_ai && presenter.profile_id
            ? avatars[presenter.profile_id] ?? null : null}
          courseIndex={courseIdx}
          usedCardIds={presenterSub
            ? (room.active_course === 1 ? presenterSub.course1_ids
               : room.active_course === 2 ? presenterSub.course2_ids
               : presenterSub.course3_ids)
            : []}
          courseText={courseText && courseText.trim()
            ? courseText
            : '(No story was written for this course.)'}
          isMyTurn={isMyTurn}
          alreadyVoted={iVoted || isMyTurn}
          yesCount={yesCount}
          noCount={noCount}
          totalVoters={totalVoters}
          canAdvance={iCanControl}
          advanceLabel={advanceLabel}
          onVote={wouldEat => {
            if (!presenterSub) return
            handleVote(presenterSub.id, room.active_course as 1 | 2 | 3, wouldEat)
          }}
          onNextCourse={handleAdvance}
          onBack={handleStepBack}
          canBack={iCanControl && room.current_player_index > 0}
        />
        {/* AI status + manual nudge — any present player can nudge, so the
            bots keep moving even if the host dropped. */}
        {iCanControl && activePlayers.some(p => p.is_ai) && (
          <div className="px-5 pb-3 space-y-2">
            {aiStatus && (
              <p className="text-xs text-center text-brand-muted">
                {aiStatus}
              </p>
            )}
            {aiVotersWaiting > 0 && (
              <button
                onClick={runAutoplay}
                className="w-full btn-outline text-sm"
              >
                {aiVotersWaiting === 1
                  ? t('Nudge 1 AI player to vote')
                  : t('Nudge {n} AI players to vote', { n: aiVotersWaiting })}
              </button>
            )}
          </div>
        )}
        {/* If the current presenter is a dropped human (not you), any
            player can skip them so the table can move on. */}
        {presenter && !presenter.is_ai && presenter.id !== me.id && (
          <div className="px-5 pb-3">
            <button
              onClick={() => handleSkipPlayer(presenter.id)}
              className="w-full btn-outline text-sm text-brand-danger"
            >
              {t('Skip {name} (dropped?)', { name: presenter.display_name })}
            </button>
          </div>
        )}
        {error && (
          <p className="text-sm text-brand-danger text-center py-2">{error}</p>
        )}
      </div>
    )
  }

  // ── Results ────────────────────────────────────────────────────────────
  if (room.status === 'results') {
    const results = players.map(p => {
      const sub = submissions.find(s => s.player_id === p.id) ?? null
      const courses = [0, 1, 2].map(i => {
        const cn = (i + 1) as 1 | 2 | 3
        const text = !sub ? '' :
          cn === 1 ? sub.course1_text :
          cn === 2 ? sub.course2_text :
                     sub.course3_text
        const yes = !sub ? 0 :
          cn === 1 ? sub.course1_votes :
          cn === 2 ? sub.course2_votes :
                     sub.course3_votes
        const no = courseVotes.filter(cv =>
          cv.submission_id === sub?.id && cv.course_number === cn && !cv.would_eat
        ).length
        return { text, yes, no }
      })
      return {
        id: p.id,
        name: p.display_name,
        isAi: p.is_ai,
        avatarUrl: !p.is_ai && p.profile_id ? avatars[p.profile_id] ?? null : null,
        totalYes: courses.reduce((s, c) => s + c.yes, 0),
        totalNo:  courses.reduce((s, c) => s + c.no,  0),
        courses,
      }
    })

    return (
      <ResultsScreen
        results={results}
        onPlayAgain={onExit}
        onBackToHub={onExit}
      />
    )
  }

  // Fallback
  return (
    <StatusScreen
      title={t('One sec...')}
      subtitle={t('Room is in an unknown state ({status})', { status: room.status })}
      onBack={onExit}
    />
  )
}

// ─── Inline sub-views ─────────────────────────────────────────────────────

interface LobbyPlayer {
  id: string
  name: string
  isAi: boolean
  isHost: boolean
  isMe: boolean
  avatarEmoji: string | null
  avatarUrl: string | null
}

function LobbyView({
  roomId, code, players, myDisplayName, isHost, busy, error, aiStatus,
  availablePersonas, addingAiId, onAddAi, onRemoveAi,
  onStart, onLeave,
}: {
  roomId: string
  code: string
  players: LobbyPlayer[]
  myDisplayName: string | null
  isHost: boolean
  busy: boolean
  error: string | null
  aiStatus: string | null
  humanCount: number
  availablePersonas: AiPlayerRow[]
  addingAiId: string | null
  onAddAi: (aiPlayerId: string) => void
  onRemoveAi: (playerId: string) => void
  onStart: () => void
  onLeave: () => void
}) {
  const t = useT()
  // Mirror of ColorfulRoomScreen lobby: TV banner up top → name input
  // → players-join card → seated players → inline AI picker → Start.
  const canStart = isHost && players.length >= 2
  const [nameDraft, setNameDraft] = useState<string>(myDisplayName ?? '')
  const [renaming, setRenaming] = useState(false)
  useEffect(() => {
    if (myDisplayName) setNameDraft(myDisplayName)
  }, [myDisplayName])

  async function saveNameIfChanged() {
    const trimmed = nameDraft.trim()
    if (!trimmed || trimmed === myDisplayName) return
    setRenaming(true)
    // supabase.rpc resolves with `{ error }` on failure rather than
    // throwing — we have to unpack `error` explicitly. Earlier code
    // wrapped this in try/catch with no unpack and silently lost the
    // error path, which is why guests' name saves felt like no-ops.
    const { error } = await supabase.rpc('rename_iet_seat', {
      p_room_id: roomId, p_name: trimmed,
    })
    setRenaming(false)
    if (error) console.warn('[iet-rename] failed', error)
  }

  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader
        title="I'd Eat That"
        subtitle={t('Room {code} · {n}/6 players · waiting to start', { code, n: players.length })}
        onBack={onLeave}
      />

      <div className="flex-1 flex flex-col px-5 py-5 gap-5 overflow-y-auto pb-36 relative z-10 glass-panes">
        {/* TV URL banner — same shape as Colorful's. Host only.
            Real <a> so host can tap to open / long-press to copy. */}
        {isHost && (
          <div className="card-lg p-4 bg-linear-to-br from-brand-text to-gray-800 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/60 mb-1">
              {t('📺 Bigger visuals? Open this on a TV / laptop')}
            </p>
            <a
              href={`https://games.jordandraper.com/tv/${code}`}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-base font-semibold break-all underline decoration-white/40 hover:decoration-white"
            >
              games.jordandraper.com/tv/{code}
            </a>
            <p className="text-xs text-white/60 mt-2">{t('Tap to open · long-press to copy/share. Optional: your phone already runs the game. This is a read-only big-screen view.')}</p>
          </div>
        )}

        {/* Your name — in-room rename. Updates both the seat snapshot
            and the host's profile in one RPC. */}
        {myDisplayName !== null && (
          <div>
            <label htmlFor="iet-room-name" className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2 block">
              {t('Your name')}
            </label>
            <input
              id="iet-room-name"
              type="text"
              value={nameDraft}
              onChange={e => setNameDraft(e.target.value.slice(0, 24))}
              onBlur={saveNameIfChanged}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
              disabled={renaming}
              maxLength={24}
              placeholder={t('Player')}
              className="w-full px-4 py-3 rounded-2xl border border-brand-border
                         bg-white text-brand-text placeholder:text-brand-muted/60
                         font-display text-lg font-bold
                         focus:outline-hidden focus:border-game-iet-dark focus:ring-2
                         focus:ring-game-iet-dark/20 transition-all disabled:opacity-60"
            />
          </div>
        )}

        {/* Players-join code — same gold-edged card the rest of IET uses. */}
        <div className="iet-gold rounded-3xl shadow-card-lg p-5
                        border-2 border-game-iet-dark text-center">
          <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-1">
            {t('Players join at')}
          </p>
          <a
            href={`https://games.jordandraper.com/join/${code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-sm text-brand-text underline decoration-brand-text/30 hover:decoration-brand-text"
          >
            games.jordandraper.com/join/{code}
          </a>
          <p className="font-display text-5xl font-black text-brand-text tracking-[0.2em] mt-3">
            {code}
          </p>
          <div className="flex justify-center mt-4">
            <RoomQR code={code} />
          </div>
        </div>

        {/* Seated players */}
        <div>
          <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
            {t('Players')}
          </p>
          <div className="space-y-2">
            {players.map(p => (
              <div key={p.id} className="card p-3 flex items-center gap-3">
                {p.isAi && p.avatarEmoji ? (
                  <div className="w-10 h-10 rounded-full bg-brand-elevated
                                  flex items-center justify-center text-xl shrink-0 relative">
                    {p.avatarEmoji}
                    <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full
                                     bg-purple-500 flex items-center justify-center text-[8px]">
                      🤖
                    </span>
                  </div>
                ) : (
                  <Avatar name={p.name} avatarUrl={p.avatarUrl} isAi={p.isAi} size="md" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-brand-text truncate">
                    {p.name}{p.isMe && <span className="text-brand-muted font-normal"> (you)</span>}
                  </p>
                  <p className="text-xs text-brand-muted">
                    {p.isHost ? 'Host' : p.isAi ? 'AI' : 'Player'}
                  </p>
                </div>
                {p.isAi && isHost && (
                  <button
                    onClick={() => onRemoveAi(p.id)}
                    className="w-7 h-7 rounded-full bg-brand-elevated text-brand-muted
                               text-sm active:scale-90 transition-transform"
                    aria-label={t('Remove {name}', { name: p.name })}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {players.length < 6 && (
              <div className="card p-3 text-center text-xs text-brand-muted">
                {t('Waiting for more players…')}
              </div>
            )}
          </div>
        </div>

        {/* Inline AI add — same shape as Colorful's. Tap a persona to seat. */}
        {isHost && availablePersonas.length > 0 && players.length < 6 && (
          <div>
            <p className="text-xs font-medium text-brand-muted uppercase tracking-wide mb-2">
              {t('Add an AI opponent')}
            </p>
            <div className="space-y-2">
              {availablePersonas.map(p => (
                <button
                  key={p.id}
                  disabled={addingAiId === p.id}
                  onClick={() => onAddAi(p.id)}
                  className="card w-full p-3 flex items-center gap-3 text-left
                             active:bg-brand-elevated transition-colors disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-full bg-brand-elevated flex
                                  items-center justify-center text-xl shrink-0">
                    {p.avatar_emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-brand-text truncate">{p.name}</p>
                    <p className="text-xs text-brand-muted truncate">{t(p.persona)}</p>
                  </div>
                  <span className="text-xs font-semibold text-game-iet-dark shrink-0">
                    {addingAiId === p.id ? '…' : '+ Add'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="floating-actions space-y-2">
        {aiStatus && (
          <p className="text-xs text-brand-muted text-center">{aiStatus}</p>
        )}
        {error && <p className="text-sm text-brand-danger text-center">{error}</p>}
        {isHost ? (
          <button
            onClick={onStart}
            disabled={!canStart || busy}
            className="btn-iet"
          >
            {busy ? 'Starting…' :
             canStart ? t('Start Game') :
             t('Need at least 2 players')}
          </button>
        ) : (
          <p className="text-center text-brand-muted text-sm animate-pulse py-3">
            {t('Waiting for the host to start…')}
          </p>
        )}
      </div>
    </div>
  )
}

function WaitingView({
  title, subtitle, isHost, canAdvance, busy, error,
  aiStatus, aiPendingSubmit, advanceLabel, onAdvance, onNudgeAi, onLeave,
  outstanding, onSkipPlayer,
}: {
  title: string
  subtitle: string
  isHost: boolean
  canAdvance: boolean
  busy: boolean
  error: string | null
  aiStatus?: string | null
  aiPendingSubmit?: number
  advanceLabel: string
  onAdvance: () => void
  onNudgeAi?: () => void
  onLeave: () => void
  outstanding?: { id: string; name: string }[]
  onSkipPlayer?: (id: string) => void
}) {
  const t = useT()
  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader title={t('Waiting')} onBack={onLeave} />
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 space-y-5">
        <div className="w-24 h-24 rounded-3xl bg-game-iet-light flex items-center justify-center text-4xl shadow-card">
          👨‍🍳
        </div>
        <div>
          <h2 className="font-display text-xl font-bold text-brand-text">{title}</h2>
          <p className="text-sm text-brand-muted mt-1 max-w-xs">{subtitle}</p>
        </div>
        {isHost && aiStatus && (
          <p className="text-xs text-brand-muted">{aiStatus}</p>
        )}
        {/* Skip dropped humans who haven't written this course, so the
            table can advance without waiting on them. */}
        {!canAdvance && onSkipPlayer && (outstanding?.length ?? 0) > 0 && (
          <div className="w-full max-w-xs space-y-1">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide">{t('Still writing')}</p>
            {outstanding!.map(o => (
              <div key={o.id} className="flex items-center gap-2">
                <span className="flex-1 text-sm text-brand-text truncate text-left">{o.name}</span>
                <button
                  onClick={() => onSkipPlayer(o.id)}
                  className="text-xs font-semibold text-brand-danger underline disabled:opacity-50"
                >
                  {t('Skip')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="floating-actions space-y-2">
        {error && <p className="text-sm text-brand-danger text-center">{error}</p>}
        {isHost && !canAdvance && (aiPendingSubmit ?? 0) > 0 && onNudgeAi && (
          <button
            onClick={onNudgeAi}
            className="btn-outline text-sm"
          >
            {aiPendingSubmit === 1
              ? t('Nudge 1 AI player to submit')
              : t('Nudge {n} AI players to submit', { n: aiPendingSubmit ?? 0 })}
          </button>
        )}
        {isHost ? (
          <button
            onClick={onAdvance}
            disabled={!canAdvance || busy}
            className="btn-primary"
          >
            {busy ? t('Moving on…') :
             canAdvance ? advanceLabel :
             t('Waiting for everyone…')}
          </button>
        ) : (
          <p className="text-center text-brand-muted text-sm animate-pulse py-3">
            {t('Waiting for the host…')}
          </p>
        )}
      </div>
    </div>
  )
}

function StatusScreen({
  title, subtitle, error, onBack,
}: {
  title: string
  subtitle?: string
  error?: string | null
  onBack: () => void
}) {
  const t = useT()
  return (
    <div className="screen">
      <BackgroundTitle text="I'd Eat That" color="#B58615" />
      <PageHeader title="I'd Eat That" onBack={onBack} />
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 space-y-3">
        <h2 className="font-display text-xl font-bold text-brand-text">{title}</h2>
        {subtitle && <p className="text-sm text-brand-muted max-w-xs">{subtitle}</p>}
        {error && <p className="text-sm text-brand-danger mt-2">{error}</p>}
      </div>
      <div className="floating-actions">
        <button onClick={onBack} className="btn-outline">{t('Back')}</button>
      </div>
    </div>
  )
}
